import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface WizardStep {
  label: string;
}

interface StepIndicatorProps {
  steps: readonly WizardStep[];
  currentStep: number;
}

/**
 * Horizontal 3-step progress bar.
 * Completed = gold (--accent) with check, current = navy (--primary) with number,
 * upcoming = muted background.
 */
export function StepIndicator({ steps, currentStep }: StepIndicatorProps) {
  return (
    <ol className="flex items-center gap-4" aria-label="Wizard progress">
      {steps.map((step, i) => {
        const state: "completed" | "current" | "upcoming" =
          i < currentStep ? "completed" : i === currentStep ? "current" : "upcoming";
        return (
          <li key={step.label} className="flex flex-1 items-center gap-3">
            <div
              aria-current={state === "current" ? "step" : undefined}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold shrink-0",
                state === "completed" &&
                  "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]",
                state === "current" &&
                  "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
                state === "upcoming" &&
                  "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
              )}
            >
              {state === "completed" ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span
              className={cn(
                "text-sm font-medium",
                state === "upcoming" && "text-[hsl(var(--muted-foreground))]",
              )}
            >
              {step.label}
            </span>
            {i < steps.length - 1 && (
              <div
                className={cn(
                  "h-px flex-1",
                  i < currentStep
                    ? "bg-[hsl(var(--accent))]"
                    : "bg-[hsl(var(--muted))]",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
